import React from 'react'
import { Stack, Table } from 'react-bootstrap'
import type { Product, UnitOfMeasure } from '@ztechapps/common.contract'
import { FormLabel, HelpTooltip, NumberInlineInput } from '@ztechapps/webui.input'
import { HorizontalStack, Icon, ShowIf, TooltipPopOver } from '@ztechapps/webui.util'
import { useUomConversionFactor } from '../select/useUomConversionFactor'
import { ConversionFactorInlineInput } from './ConversionFactorInlineInput'
import { BaseUomSelectInlineInput } from './BaseUomSelectInlineInput'
import { ContextUomSelectInlineInput } from './ContextUomSelectInlineInput'
import { UomDisplay } from '../../QuantityWithUom'

type ProductUnitOfMeasureProps = {
  product: Product
  InlineInputComponent: React.ComponentType<any>
  canEdit?: boolean
  /** Additional props to pass to each InlineInputComponent (e.g., productDocument for ProductTooltipPopOverInlineInput) */
  inlineInputProps?: Record<string, unknown>
}

type ConversionRowProps = {
  label: string
  product: Product
  uomFieldName: string
  factorFieldName: string
  queryName: string
  InlineInputComponent: React.ComponentType<any>
  canEdit?: boolean
  inlineInputProps?: Record<string, unknown>
}

/**
 * Table row for UOM conversion: "1 [UOM select] = [factor input] [base symbol]"
 */
const ConversionRow = ({
  label,
  product,
  uomFieldName,
  factorFieldName,
  queryName,
  InlineInputComponent,
  canEdit,
  inlineInputProps,
}: ConversionRowProps) => {
  const baseUom = product?.baseUom as UnitOfMeasure | undefined
  const contextUom = product?.[uomFieldName] as UnitOfMeasure | undefined
  const { calculatedFactor, isCrossCategory } = useUomConversionFactor(baseUom, contextUom)

  const baseSymbol = baseUom?.symbol || '---'

  return (
    <tr>
      <td className="align-middle">
        <FormLabel>{label}</FormLabel>
      </td>
      <td>
        <HorizontalStack gap={2} className="justify-content-between">
          <HorizontalStack gap={1}>
            <div>1</div>
            <InlineInputComponent
              parentInputComponent={ContextUomSelectInlineInput}
              name={uomFieldName}
              queryName={queryName}
              canEdit={canEdit}
              factorFieldName={factorFieldName}
              baseUom={baseUom}
              {...inlineInputProps}
            />
          </HorizontalStack>
          <div> = </div>
        </HorizontalStack>
      </td>
      <td>
        <HorizontalStack gap={2}>
          <InlineInputComponent
            parentInputComponent={ConversionFactorInlineInput}
            name={factorFieldName}
            canEdit={canEdit}
            calculatedFactor={calculatedFactor}
            {...inlineInputProps}
            displayContent={(qty) => {
              return (
                <HorizontalStack gap={0}>
                  <div>{qty}</div>
                  <UomDisplay>{baseSymbol}</UomDisplay>
                </HorizontalStack>
              )
            }}
          />
          <ShowIf condition={isCrossCategory}>
            <TooltipPopOver
              tooltipBody={
                <div>
                  <div className="fw-semibold mb-1">Cross-Category Unit of Measure</div>
                  <div className="mb-2">
                    The selected unit belongs to a different category than the base unit, so automatic conversion
                    calculation is not available.
                  </div>
                  <div className="mb-2">
                    This is valid for items measured differently across contexts. For example, fabric may be tracked in
                    inventory by weight (kg) but sold by length (meters), or wire purchased by weight but issued by
                    length.
                  </div>
                  <div>Please enter the conversion factor manually based on your product specifications.</div>
                </div>
              }
              width={350}
            >
              <Icon name="exclamation-triangle" className="text-warning" />
            </TooltipPopOver>
          </ShowIf>
        </HorizontalStack>
      </td>
    </tr>
  )
}

export const ProductUnitOfMeasure = ({
  product,
  InlineInputComponent,
  canEdit = true,
  inlineInputProps,
}: ProductUnitOfMeasureProps) => {
  return (
    <Stack gap={3}>
      {/* Conversion table */}
      <Table size="sm" className="mx-0">
        <thead>
          <tr className="table-warning">
            <th style={{ width: '45%' }}>
              <FormLabel>Inventory/Base Unit: </FormLabel>
            </th>
            <th style={{ width: '35%' }}>
              <InlineInputComponent
                parentInputComponent={BaseUomSelectInlineInput}
                name="baseUom"
                queryName="baseUom { _id code name symbol category conversionFactor } orderUom { _id code name symbol category conversionFactor } purchaseUom { _id code name symbol category conversionFactor } workOrderUom { _id code name symbol category conversionFactor } orderConversionFactor purchaseConversionFactor workOrderConversionFactor"
                canEdit={canEdit}
                {...inlineInputProps}
              />
            </th>
            <th style={{ width: '20%' }}>
              <HorizontalStack>
                <InlineInputComponent
                  parentInputComponent={NumberInlineInput}
                  name="unitOfMeasurePrecision"
                  canEdit={canEdit}
                  placeholder="0"
                  {...inlineInputProps}
                />
                <HelpTooltip help={'Decimal precision'} />
              </HorizontalStack>
            </th>
          </tr>
        </thead>
        <tbody>
          <ConversionRow
            label="Selling"
            product={product}
            uomFieldName="orderUom"
            factorFieldName="orderConversionFactor"
            queryName="orderUom { _id code name symbol category conversionFactor } orderConversionFactor"
            InlineInputComponent={InlineInputComponent}
            canEdit={canEdit}
            inlineInputProps={inlineInputProps}
          />
          <ConversionRow
            label="Purchasing"
            product={product}
            uomFieldName="purchaseUom"
            factorFieldName="purchaseConversionFactor"
            queryName="purchaseUom { _id code name symbol category conversionFactor } purchaseConversionFactor"
            InlineInputComponent={InlineInputComponent}
            canEdit={canEdit}
            inlineInputProps={inlineInputProps}
          />
          <ConversionRow
            label="Manufacturing"
            product={product}
            uomFieldName="workOrderUom"
            factorFieldName="workOrderConversionFactor"
            queryName="workOrderUom { _id code name symbol category conversionFactor } workOrderConversionFactor"
            InlineInputComponent={InlineInputComponent}
            canEdit={canEdit}
            inlineInputProps={inlineInputProps}
          />
        </tbody>
      </Table>
    </Stack>
  )
}
